import { defineStore } from 'pinia';
import { LangType, langCode } from '~/types/lang';
import { LANG_HE, LANG_EN } from '~/config/langCode';
import { LANG } from '~/config/lang';
import { usePopup } from '~/store/usePopup';
import { useApplyError } from '~/store/useApplyError';
import {
  IJobsByShortLink,
  IRegion,
  IGetJobsQuery,
  IJobsItemInfo,
  ITagWordWithCounter,
} from '~/types/jobs';
import { GLobalSettings } from '@/environmentSettings';

// import { fa } from '@faker-js/faker';

const { pageSize } = GLobalSettings.development.runtimeConfig.public;

interface IJobsState {
  lang: LangType;
  jobs: IJobsItemInfo[] | undefined;
  apiData: IJobsByShortLink | null;
  tags: string[];
  tagsWithCounter: ITagWordWithCounter[];
  regions: IRegion[];
  loading: boolean;
  getRegions: boolean;
  getTagWords: boolean;
  regionIds?: number[];
  tagWords?: string[];
  languageId?: number;
  shortlinkId?: string;
  countryCode?: string;
  shortlinkBaseUrl?: string;
  fromRow: number;
}

export const useJobs = defineStore('jobs', {
  state: (): IJobsState => {
    return {
      lang: 'he' as LangType,
      jobs: undefined,
      apiData: null,
      tags: [],
      tagsWithCounter: [],
      regions: [],
      loading: false,
      getRegions: true,
      getTagWords: true,
      fromRow: 1,
    };
  },
  getters: {
    // fromRow(state): number {
    //   return (state.jobs?.length ?? 0) + 1;
    // },
    toRow(): number {
      return this.fromRow ? this.fromRow + pageSize : pageSize;
    },
    query(state): IGetJobsQuery {
      return {
        ShortlinkBaseUrl: state.shortlinkBaseUrl,
        ShortlinkId: state.shortlinkId,
        CountryCode: state.countryCode,
        LanguageId: state.languageId,
        Filter: {
          FromRow: this.fromRow,
          ToRow: this.toRow,
          RegionIds: state.regionIds,
          TagWords: state.tagWords,
        },
        MetadataSelection: {
          GetRegions: state.getRegions,
          GetTagWords: state.getTagWords,
        },
      };
    },
    endOfResults: (state): boolean =>
      !!state?.apiData?.Payload?.TotalCount &&
      state.fromRow >= state.apiData.Payload.TotalCount,
    dir: (state) => {
      return state.lang === 'he' ? 'rtl' : 'ltr';
    },
    langCode: (state): langCode => (state.lang === 'he' ? 1037 : 1033),
  },
  actions: {
    resetFromRow() {
      this.fromRow = 1;
    },
    async fetchData(data: IJobsByShortLink) {
      const popupStore = usePopup();
      const applyErrorStore = useApplyError();
      if (!data) {
        popupStore.modalError = true;
        popupStore.modalText = LANG.errorFetchingData[this.lang];
        popupStore.open = true;
        return;
      }
      this.fromRow += pageSize;
      try {
        this.apiData = data;
        if (this.apiData.Payload.JobsGetResponseStatusEnum > 0) {
          throw new Error(
            this.apiData.Payload.JobsGetResponseStatusEnum === 1
              ? 'MissingLicense'
              : 'ShortlinkNotFound'
          );
        }

        if (this.apiData?.Payload?.Regions?.length > 0) {
          this.regions = this.apiData.Payload.Regions;
          this.query?.MetadataSelection &&
            (this.query.MetadataSelection.GetRegions = false);
        }
        if (this.apiData?.Payload?.TagWords?.length > 0) {
          this.tags = this.apiData.Payload.TagWords;
          this.query?.MetadataSelection &&
            (this.query.MetadataSelection.GetTagWords = false);
        }
        if (this.apiData?.Payload?.TagWordsWithCounter?.length > 0) {
          this.tagsWithCounter = this.apiData.Payload.TagWordsWithCounter;
        }
        if (data?.Payload?.Jobs) {
          this.jobs = this.jobs
            ? [...this.jobs, ...data.Payload.Jobs]
            : data.Payload.Jobs;
        }
        // remove duplicate jobs by jobId
        this.jobs = this.jobs?.filter(
          (job, index, self) =>
            index === self.findIndex((j) => j.JobId === job.JobId)
        );

        // const duplicateJobs = this.jobs?.filter(
        //   (job, index, self) =>
        //     index !== self.findIndex((j) => j.JobId === job.JobId)
        // );
      } catch (error) {
        console.debug({ fetchDataError: error });
        popupStore.modalError = true;
        popupStore.modalText = LANG.errorFetchingData[this.lang];
        popupStore.open = true;
      }
    },
    clearApiData() {
      this.fromRow = 1;
      this.jobs = [];

      this.apiData?.Payload.Jobs && (this.apiData.Payload.Jobs = []);
      this.apiData?.Payload.TotalCount && (this.apiData.Payload.TotalCount = 0);
      // this.apiData?.Payload.Regions && (this.apiData.Payload.Regions = []);
      // this.apiData?.Payload.TagWords && (this.apiData.Payload.TagWords = []);
      this.apiData?.Payload.JobsGetResponseStatusEnum &&
        (this.apiData.Payload.JobsGetResponseStatusEnum = 0);
    },
  },
});
